/**
 * Mobile filter sheet container (Figma redesign)
 *
 * Manages collapsible section state for BASIC, FINANCIAL, FIT & RATINGS,
 * PERFORMANCE, and GOALKEEPER sections. BASIC and FINANCIAL default open;
 * score sections default closed. Also owns the role chip expansion toggle.
 * Delegates all filter data and callbacks to the View.
 * @module components/shared/MobileFilterSheet/MobileFilterSheetContainer
 */

import React, { memo, useCallback, useMemo, useState } from "react";

import { useSafeAreaInsets } from "react-native-safe-area-context";

import MobileFilterSheetView from "./MobileFilterSheetView";
import countSectionFilters from "@/features/scouting/utils/countSectionFilters";
import { useTheme } from "@/providers/ThemeProvider";

import type {
  CoreRangeFilterProps,
  FilterOption,
  ReportStatsFilterProps,
  ScoreRangeFilterProps,
} from "@/types/filter";
import type { LeaguePresetOption } from "@/features/player-filter/utils/leaguePresets";

/** Presentation mode for the filter sheet wrapper */
export type FilterSheetPresentation = "actionsheet" | "modal";

/** Props for MobileFilterSheetContainer */
interface MobileFilterSheetContainerProps
  extends CoreRangeFilterProps, ScoreRangeFilterProps, ReportStatsFilterProps {
  /** Wrapper component: "actionsheet" (bottom sheet) or "modal" (full-screen overlay) */
  readonly presentation?: FilterSheetPresentation;
  /** Whether the action sheet is visible */
  readonly isOpen: boolean;
  /** Callback to close the action sheet */
  readonly onClose: () => void;
  /** Callback when the "Apply Filters" button is pressed */
  readonly onApply: () => void;
  /** Callback when the "CLEAR ALL" button is pressed */
  readonly onClearAll: () => void;
  /** Total active filter count — drives CLEAR ALL disabled state */
  readonly activeFilterCount: number;
  /**
   * Live count of players matching the current (pending) filter state, shown
   * as "Show Results [N]" in the footer (SE-5082). Omit to show "Show Results"
   * with no count.
   */
  readonly resultCount?: number | null;
  /**
   * Disables the primary "Show Results" button (e.g. when no filters are
   * active — SE-4146 AC#3). Defaults to enabled.
   */
  readonly applyDisabled?: boolean;
  /** Optional testID for the footer "Discard"/"Cancel" button (e2e). */
  readonly discardTestID?: string;
  /** Optional testID for the footer "Show Results" primary button (e2e). */
  readonly showResultsTestID?: string;

  /* --- Role chips --- */
  readonly roleOptions: readonly FilterOption[];
  readonly selectedRoleArchetypes: readonly string[];
  readonly onToggleRole: (id: string) => void;
  readonly onClearRoles: () => void;

  /* --- Multi-select dropdowns (consistent with web FilterSheet) --- */
  readonly positionOptions: readonly FilterOption[];
  readonly selectedPositionIds: readonly string[];
  readonly onTogglePosition: (id: string) => void;
  readonly onClearPositions: () => void;

  readonly leagueOptions: readonly LeaguePresetOption[];
  readonly selectedLeagueIds: readonly string[];
  readonly onToggleLeague: (id: string) => void;
  readonly onClearLeagues: () => void;
  readonly onChangeLeaguePreset?: (ids: readonly string[]) => void;

  readonly competitionOptions?: readonly FilterOption[];
  readonly selectedCompetitionIds?: readonly string[];
  readonly onToggleCompetition?: (id: string) => void;
  readonly onClearCompetitions?: () => void;

  readonly clubOptions: readonly FilterOption[];
  readonly selectedClubIds: readonly string[];
  readonly onToggleClub: (id: string) => void;
  readonly onClearClubs: () => void;

  readonly nationalityOptions: readonly FilterOption[];
  readonly selectedNationalityIds: readonly string[];
  readonly onToggleNationality: (id: string) => void;
  readonly onClearNationalities: () => void;

  /* --- Club server-side search --- */
  /** Current club search text for server-side filtering */
  readonly clubSearchText?: string;
  /** Callback to update the club search text */
  readonly onClubSearchChange?: (text: string) => void;
  /** Whether club search query is loading */
  readonly clubSearching?: boolean;
}

/**
 * No-op fallback for optional club search callback.
 * @param _text - Search text (unused)
 */
const NOOP_SEARCH_CHANGE = (_text: string): void => {};
/**
 * No-op fallback for optional categorical filter toggles.
 * @param _id - Selected option ID (unused)
 */
const NOOP_TOGGLE = (_id: string): void => {};
/** No-op fallback for optional categorical filter clearing. */
const NOOP_CLEAR = (): void => {};
const EMPTY_OPTIONS: readonly FilterOption[] = [];
const EMPTY_IDS: readonly string[] = [];

/**
 * Container for the mobile filter action sheet.
 * Owns collapsible section state and role chip expansion toggle.
 * @param props - Component props (see MobileFilterSheetContainerProps)
 */
const MobileFilterSheetContainer = (props: MobileFilterSheetContainerProps) => {
  const { presentation = "actionsheet" } = props;
  const isDark = useTheme().resolvedTheme === "dark";
  const insets = useSafeAreaInsets();

  const sectionCounts = useMemo(
    () =>
      countSectionFilters({
        selectedRoleArchetypes: props.selectedRoleArchetypes,
        selectedPositionIds: props.selectedPositionIds,
        selectedClubIds: props.selectedClubIds,
        selectedScoutIds: [],
        selectedLeagueIds: props.selectedLeagueIds,
        selectedCompetitionIds: props.selectedCompetitionIds ?? EMPTY_IDS,
        selectedNationalityIds: props.selectedNationalityIds,
        ageRange: props.ageRange,
        contractMonthsRange: props.contractMonthsRange,
        valuationRange: props.valuationRange,
        gprRange: props.gprRange,
        fitScoreRange: props.fitScoreRange,
        teamStyleFitRange: props.teamStyleFitRange,
        carryingRange: props.carryingRange,
        dribblingRange: props.dribblingRange,
        passingRange: props.passingRange,
        shootingRange: props.shootingRange,
        gkPassingRange: props.gkPassingRange,
        gkShotStoppingRange: props.gkShotStoppingRange,
        gkPositioningRange: props.gkPositioningRange,
        customStatRanges: props.customStatRanges,
      }),
    [
      props.selectedRoleArchetypes,
      props.selectedPositionIds,
      props.selectedClubIds,
      props.selectedLeagueIds,
      props.selectedCompetitionIds,
      props.selectedNationalityIds,
      props.ageRange,
      props.contractMonthsRange,
      props.valuationRange,
      props.gprRange,
      props.fitScoreRange,
      props.teamStyleFitRange,
      props.carryingRange,
      props.dribblingRange,
      props.passingRange,
      props.shootingRange,
      props.gkPassingRange,
      props.gkShotStoppingRange,
      props.gkPositioningRange,
      props.customStatRanges,
    ]
  );

  const [isBasicOpen, setIsBasicOpen] = useState(true);
  const [isFinancialOpen, setIsFinancialOpen] = useState(true);
  const [isFitRatingsOpen, setIsFitRatingsOpen] = useState(false);
  const [isPerformanceOpen, setIsPerformanceOpen] = useState(false);
  const [isGoalkeeperOpen, setIsGoalkeeperOpen] = useState(false);
  const [isReportStatsOpen, setIsReportStatsOpen] = useState(false);
  const [isRoleExpanded, setIsRoleExpanded] = useState(false);

  const handleToggleBasic = useCallback(
    () => setIsBasicOpen(prev => !prev),
    []
  );
  const handleToggleFinancial = useCallback(
    () => setIsFinancialOpen(prev => !prev),
    []
  );
  const handleToggleFitRatings = useCallback(
    () => setIsFitRatingsOpen(prev => !prev),
    []
  );
  const handleTogglePerformance = useCallback(
    () => setIsPerformanceOpen(prev => !prev),
    []
  );
  const handleToggleGoalkeeper = useCallback(
    () => setIsGoalkeeperOpen(prev => !prev),
    []
  );
  const handleToggleReportStats = useCallback(
    () => setIsReportStatsOpen(prev => !prev),
    []
  );
  const handleToggleRoleExpanded = useCallback(
    () => setIsRoleExpanded(prev => !prev),
    []
  );

  return (
    <MobileFilterSheetView
      presentation={presentation}
      isDark={isDark}
      topInset={insets.top}
      bottomInset={insets.bottom}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onApply={props.onApply}
      onClearAll={props.onClearAll}
      activeFilterCount={props.activeFilterCount}
      resultCount={props.resultCount ?? null}
      applyDisabled={props.applyDisabled ?? false}
      discardTestID={props.discardTestID}
      showResultsTestID={props.showResultsTestID}
      sectionCounts={sectionCounts}
      isBasicOpen={isBasicOpen}
      onToggleBasic={handleToggleBasic}
      isFinancialOpen={isFinancialOpen}
      onToggleFinancial={handleToggleFinancial}
      isFitRatingsOpen={isFitRatingsOpen}
      onToggleFitRatings={handleToggleFitRatings}
      isPerformanceOpen={isPerformanceOpen}
      onTogglePerformance={handleTogglePerformance}
      isGoalkeeperOpen={isGoalkeeperOpen}
      onToggleGoalkeeper={handleToggleGoalkeeper}
      roleOptions={props.roleOptions}
      selectedRoleArchetypes={props.selectedRoleArchetypes}
      onToggleRole={props.onToggleRole}
      onClearRoles={props.onClearRoles}
      isRoleExpanded={isRoleExpanded}
      onToggleRoleExpanded={handleToggleRoleExpanded}
      positionOptions={props.positionOptions}
      selectedPositionIds={props.selectedPositionIds}
      onTogglePosition={props.onTogglePosition}
      onClearPositions={props.onClearPositions}
      leagueOptions={props.leagueOptions}
      selectedLeagueIds={props.selectedLeagueIds}
      onToggleLeague={props.onToggleLeague}
      onClearLeagues={props.onClearLeagues}
      onChangeLeaguePreset={props.onChangeLeaguePreset}
      competitionOptions={props.competitionOptions ?? EMPTY_OPTIONS}
      selectedCompetitionIds={props.selectedCompetitionIds ?? EMPTY_IDS}
      onToggleCompetition={props.onToggleCompetition ?? NOOP_TOGGLE}
      onClearCompetitions={props.onClearCompetitions ?? NOOP_CLEAR}
      clubOptions={props.clubOptions}
      selectedClubIds={props.selectedClubIds}
      onToggleClub={props.onToggleClub}
      onClearClubs={props.onClearClubs}
      nationalityOptions={props.nationalityOptions}
      selectedNationalityIds={props.selectedNationalityIds}
      onToggleNationality={props.onToggleNationality}
      onClearNationalities={props.onClearNationalities}
      clubSearchText={props.clubSearchText ?? ""}
      onClubSearchChange={props.onClubSearchChange ?? NOOP_SEARCH_CHANGE}
      clubSearching={props.clubSearching ?? false}
      ageRange={props.ageRange}
      onSetAgeRange={props.onSetAgeRange}
      valuationRange={props.valuationRange}
      onSetValuationRange={props.onSetValuationRange}
      contractMonthsRange={props.contractMonthsRange}
      onSetContractMonthsRange={props.onSetContractMonthsRange}
      gprRange={props.gprRange}
      onSetGprRange={props.onSetGprRange}
      fitScoreRange={props.fitScoreRange}
      onSetFitScoreRange={props.onSetFitScoreRange}
      teamStyleFitRange={props.teamStyleFitRange}
      onSetTeamStyleFitRange={props.onSetTeamStyleFitRange}
      carryingRange={props.carryingRange}
      onSetCarryingRange={props.onSetCarryingRange}
      dribblingRange={props.dribblingRange}
      onSetDribblingRange={props.onSetDribblingRange}
      passingRange={props.passingRange}
      onSetPassingRange={props.onSetPassingRange}
      shootingRange={props.shootingRange}
      onSetShootingRange={props.onSetShootingRange}
      gkPassingRange={props.gkPassingRange}
      onSetGkPassingRange={props.onSetGkPassingRange}
      gkShotStoppingRange={props.gkShotStoppingRange}
      onSetGkShotStoppingRange={props.onSetGkShotStoppingRange}
      gkPositioningRange={props.gkPositioningRange}
      onSetGkPositioningRange={props.onSetGkPositioningRange}
      isReportStatsOpen={isReportStatsOpen}
      onToggleReportStats={handleToggleReportStats}
      customMetrics={props.customMetrics}
      customStatRanges={props.customStatRanges}
      onSetCustomStatRange={props.onSetCustomStatRange}
    />
  );
};

MobileFilterSheetContainer.displayName = "MobileFilterSheetContainer";

export default memo(MobileFilterSheetContainer);
